import { API } from "config";
import request from "utilities/request";

const fileApiBaseUrl = API.fileApi.url;

export type ShareLinkStatus = "active" | "expired" | "revoked";

export interface ShareLinkResponse {
  token: string;
  url: string;
  expires_at: string;
}

export interface ShareLinkListItem {
  token: string;
  url: string;
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
  status: ShareLinkStatus;
}

/**
 * Get-or-mint a share link for a file (file-api ADR 036). Idempotent per
 * (file_id, caller) — repeat calls renew the same token's expiry rather than
 * minting a new one, so this always returns the one active link for the file.
 */
export async function mintShareLink(
  fileId: string,
  getAccessToken: () => Promise<string>
): Promise<ShareLinkResponse> {
  const accessToken = await getAccessToken();
  const url = `${fileApiBaseUrl}/api/v1/files/${fileId}/shares`;

  const options = {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };

  const response = await request(url, options);
  return response.json();
}

/**
 * List share links for a file, most recent first. Used to check whether an
 * active link already exists (at most one) so the UI can offer "Stop sharing".
 */
export async function listShareLinks(
  fileId: string,
  getAccessToken: () => Promise<string>
): Promise<ShareLinkListItem[]> {
  const accessToken = await getAccessToken();
  const url = `${fileApiBaseUrl}/api/v1/files/${fileId}/shares`;

  const options = {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };

  const response = await request(url, options);
  return response.json();
}

/**
 * Revoke a share link. Soft delete — does not affect copies already imported
 * by recipients.
 */
export async function revokeShareLink(
  fileId: string,
  token: string,
  getAccessToken: () => Promise<string>
): Promise<void> {
  const accessToken = await getAccessToken();
  const url = `${fileApiBaseUrl}/api/v1/files/${fileId}/shares/${token}`;

  const options = {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };

  await request(url, options);
}
